/**
 * Types for the Menu Cards / "Reports A La Carte" feature.
 *
 * These mirror the response shapes from `api/src/functions/menuCards.ts`
 * one-for-one. Keep in sync — server is the source of truth.
 */

/** A row from agg.MenuCard_Services — one per uniquely-identified service. */
export interface MenuCardService {
  service_date: string;          // "2026-05-02"
  service_time_label: string;    // "2026-05-02 12:35"   (PK component — distinguishes lunch vs evening)
  journey_description: string;   // "Spring Service - Departs Pickering 12:35"
  journey_type: string;          // "North Yorkshire Pullman"
  service_day_label: string;     // "Sat 02 May 2026"
  booking_count: number;         // bookings = number of menu cards to print
  total_diners: number;          // SUM(footfall) across the bookings
  carriages_used: string | null; // "Garnet, Jos De Crau, Opal, Robin"
  carriage_count: number;
  has_dietary_count: number;     // count of bookings with dietary flags
  refreshed_at: string | null;   // when the agg row was rebuilt
}

/** Last successful refresh of the menu-cards data (from dbo.RefreshLog). */
export interface MenuCardsLastRefresh {
  at: string | null;             // ISO datetime
  status: string;                // 'success' | 'error' | 'partial'
  runner: string | null;         // 'ONTRACK (pullman)' typically
  duration_ms: number | null;
}

/** Most-recent row from app.RefreshRequests for kind='menu-cards'. */
export interface MenuCardsLastRequest {
  id: number;
  status: RefreshRequestStatus;
  requested_at: string | null;
  requested_by: string;          // UPN
  completed_at: string | null;
  message: string | null;
}

/** Response shape for GET /api/dashboards/pullman/menu-card-services?from=&to= */
export interface MenuCardServicesResponse {
  from: string | null;
  to: string | null;
  data_range: { earliest: string | null; latest: string | null };
  services: MenuCardService[];
  last_refresh: MenuCardsLastRefresh | null;
  last_request: MenuCardsLastRequest | null;
}

/** Booking-level row — one menu card per row, ready to render. */
export interface MenuCardBooking {
  id: number;
  booking_reference: number;
  account_name: string;
  account_telephone: string | null;
  service_date: string;
  service_day_label: string;
  service_time_label: string;
  journey_description: string;
  journey_type: string;
  carriage: string;              // "Pullman - Garnet"
  table_name: string;
  seats: string;                 // "B1,B2,B3,B4"
  seats_reserved: number | null;
  footfall: number | null;
  qty: number | null;
  menus: string;                 // multi-line, "\n"-separated
  booking_notes: string | null;
  has_dietary_notes: boolean;
  /** The catering team's edited dietary comment (cache.MenuCardDietaryOverrides).
   *  When present the card prints THIS in its DIETARY NOTES banner instead of
   *  booking_notes, and the Pullman refresh never overwrites it. */
  dietary_override: string | null;
  dietary_updated_by: string | null;
  dietary_updated_at: string | null;
}

/** One booking's dietary-comment save: non-empty text = override; empty = reset to MERAC. */
export interface DietaryOverrideOp {
  booking_reference: number;
  service_date: string;
  text: string | null;
}

/** Service identifier echoed back from GET /menu-cards. */
export interface MenuCardServiceHeader {
  service_date: string;
  service_time_label: string;
  journey_description: string;
  journey_type: string | null;
  service_day_label: string | null;
}

/** Response shape for GET /api/dashboards/pullman/menu-cards */
export interface MenuCardsForServiceResponse {
  service: MenuCardServiceHeader;
  bookings: MenuCardBooking[];
}

/** Response shape for GET /api/dashboards/pullman/menu-card-bookings/search */
export interface MenuCardBookingsSearchResponse {
  q: string;
  from: string;
  to: string;
  limit: number;
  truncated: boolean; // true when the result set hit the limit cap
  bookings: MenuCardBooking[];
}

/** Refresh-request lifecycle. */
export type RefreshRequestStatus =
  | "pending"
  | "running"
  | "completed"
  | "error";

/** Response shape for POST /api/dashboards/pullman/refresh-menu-cards. */
export interface RefreshMenuCardsPostResponse {
  id: number;
  status: RefreshRequestStatus;
  requested_at: string | null;
  requested_by: string;
  already_in_flight: boolean;
}

/** Response shape for GET /api/dashboards/pullman/refresh-requests/{id}. */
export interface RefreshRequestStatusResponse {
  id: number;
  kind: string;
  requested_at: string | null;
  requested_by: string;
  started_at: string | null;
  completed_at: string | null;
  status: RefreshRequestStatus;
  message: string | null;
  rows_total: number | null;
  duration_ms: number | null;
}

// ---------------------------------------------------------------------------
// Print-time options (UI state only — never sent to server in R3).
// ---------------------------------------------------------------------------

/**
 * Five hand-built art deco border styles + a "mix randomly" option that
 * deterministically picks one per booking using the BookingReference as
 * the seed (so reprints come out identical).
 */
export type TemplateStyle =
  | "pullman-classic"        // gold chevrons, arched header, fluted columns
  | "locomotive-sunburst"    // streamlined loco silhouette, radiating sunburst
  | "carriage-panelling"     // wood-veneer-style inlaid panels, brass corners
  | "streamline-moderne"     // flowing speed-lines, stylised smoke trails
  | "art-deco-bouquet"       // fan/lotus motifs, engine-wheel rosettes
  | "plain-legacy"           // faithful repro of the old SSRS card — plain, unbranded
  | "travel-treat"           // simpler card for Travel & Treats (no coach/table/courses)
  | "random-mix";            // deterministic shuffle — different style per card

export type PrintMode = "colour" | "bw";

/**
 * Physical card size.
 *   - "a6": A6 portrait (105×148mm) — the original single-column card.
 *   - "a5": A5 landscape (210×148mm) — a larger two-panel card, guest/service
 *     detail on the left and the full pre-ordered menu given its own column on
 *     the right. Mirrors the layout intent of the legacy "Menu Cards - A5" RDL.
 * Orthogonal to `template` (the decorative style) and `printMode`.
 */
export type CardSize = "a6" | "a5";

export interface MenuCardPrintOptions {
  template: TemplateStyle;
  size: CardSize;
  printMode: PrintMode;
  /**
   * If true, a banner is rendered at the top of any card whose booking has
   * `has_dietary_notes = true`, with the contents of `booking_notes` shown
   * verbatim. Defaults to true (kitchen-relevant).
   */
  highlightDietary: boolean;
  /**
   * If true, the menu order lines are scaled (font size + line spacing) to
   * better fill the card's menu area — larger and more spread out when a
   * booking has only a few lines, gently compressed when it has many. Off is
   * the fixed classic sizing. Affects the Pullman menu cards (A6 + A5).
   */
  scaleFoodToFit: boolean;
}

export const DEFAULT_PRINT_OPTIONS: MenuCardPrintOptions = {
  template: "random-mix",
  size: "a6",
  printMode: "colour",
  highlightDietary: true,
  scaleFoodToFit: false,
};

/**
 * Display metadata for each template — used to render the radio-tile selector
 * AND (later) as a stable lookup keyed off `TemplateStyle` for the actual
 * SVG renderer in R4. Kept in this file so the modal and the renderer share
 * a single registry and can never drift.
 */
export interface TemplateDescriptor {
  key: TemplateStyle;
  name: string;
  blurb: string;
}

export const TEMPLATE_REGISTRY: TemplateDescriptor[] = [
  {
    key: "pullman-classic",
    name: "Pullman Classic",
    blurb: "Gold chevrons and an arched header — the most formal of the five.",
  },
  {
    key: "locomotive-sunburst",
    name: "Locomotive Sunburst",
    blurb: "A streamlined loco bursting through radiating rays of sunburst.",
  },
  {
    key: "carriage-panelling",
    name: "Carriage Panelling",
    blurb: "Inlaid wood-veneer panels with brass-fitting corners — pure 1930s.",
  },
  {
    key: "streamline-moderne",
    name: "Streamline Moderne",
    blurb: "Flowing speed-lines and curling smoke trails — the most dynamic.",
  },
  {
    key: "art-deco-bouquet",
    name: "Art Deco Bouquet",
    blurb: "Fan and lotus motifs, engine-wheel rosettes — most decorative.",
  },
  {
    key: "plain-legacy",
    name: "Plain (legacy)",
    blurb: "A faithful repro of the old SSRS card — plain type, no branding. Cheapest to print; best on A5.",
  },
  {
    key: "random-mix",
    name: "Mix randomly",
    blurb: "Each card gets a different template — chosen deterministically by booking reference.",
  },
];

/**
 * Display metadata for each card size — powers the size selector, same pattern
 * as `TEMPLATE_REGISTRY`. `size` is orthogonal to `template`: any style can be
 * printed at either size.
 */
export interface CardSizeDescriptor {
  key: CardSize;
  name: string;
  blurb: string;
}

export const CARD_SIZE_REGISTRY: CardSizeDescriptor[] = [
  {
    key: "a6",
    name: "A6 portrait",
    blurb: "The classic small card — one per cover, placed on the table.",
  },
  {
    key: "a5",
    name: "A5 landscape",
    blurb: "A larger two-panel card — guest details on the left, the full menu on the right.",
  },
];
